import { React, Fragment, useState, useEffect } from 'libs/react';
import { useLazyQuery } from '@apollo/client';
import { Button } from 'components/Button';
import { Modal } from 'components/modal/Modal';
import { FormSelect } from 'components/form/FormSelect';
import { Spinner } from 'components/Spinner';
import { GetMewsRatesQuery } from 'graphql/Deal/queries/GetMewsRatesQuery';
import { GetDealConnectorRefQuery } from 'graphql/Deal/queries/GetDealConnectorRefQuery';
import { SaleItemConnectorRefsQuery } from 'graphql/SaleItemConnectorRef/queries/SaleItemConnectorRefsQuery';
import './DealMewsAvailabilityBlockModal.scss';
/**
 * @component DealMewsAvailabilityBlockModal
 * @since 1.0.0
 */
export function DealMewsAvailabilityBlockModal(props) {
    var _a, _b, _c;
    const { dealId, dealItems, mewsAssetId, onClose, onConfirm } = props;
    const [rateId, setRateId] = useState(null);
    const [existingBlockRef, setExistingBlockRef] = useState(null);
    const [mappedOptionIds, setMappedOptionIds] = useState([]);
    // IDs for rates (main DealItem SaleItems)
    const mainSaleItemIds = [...new Set(dealItems.map((di) => di.item.id))];
    // IDs of all option SaleItems across all DealItems
    const optionSaleItemIds = [
        ...new Set(dealItems.flatMap((di) => { var _a; return ((_a = di.options) !== null && _a !== void 0 ? _a : []).map((o) => o.item.id); }))
    ];
    const [fetchRates, { data: ratesData, loading: loadingRates }] = useLazyQuery(GetMewsRatesQuery, {
        fetchPolicy: 'network-only'
    });
    const [fetchDealConnectorRef, { loading: loadingConnectorRef }] = useLazyQuery(GetDealConnectorRefQuery, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            var _a, _b;
            setExistingBlockRef((_b = (_a = data === null || data === void 0 ? void 0 : data.getDealConnectorRef) === null || _a === void 0 ? void 0 : _a.ref) !== null && _b !== void 0 ? _b : null);
        }
    });
    // Main items → serviceId → rates
    const [fetchMainRefs] = useLazyQuery(SaleItemConnectorRefsQuery, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            var _a, _b, _c;
            const ref = (_a = data === null || data === void 0 ? void 0 : data.saleItemConnectorRefs) === null || _a === void 0 ? void 0 : _a.find((r) => r.assetId === mewsAssetId);
            const serviceId = (_c = (_b = ref === null || ref === void 0 ? void 0 : ref.serviceId) !== null && _b !== void 0 ? _b : ref === null || ref === void 0 ? void 0 : ref.ref) !== null && _c !== void 0 ? _c : null;
            if (serviceId) {
                fetchRates({ variables: { input: { serviceId } } });
            }
        }
    });
    // Option items → which ones have a connector ref for mews
    const [fetchOptionRefs, { loading: loadingOptionRefs }] = useLazyQuery(SaleItemConnectorRefsQuery, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            var _a;
            const ids = ((_a = data === null || data === void 0 ? void 0 : data.saleItemConnectorRefs) !== null && _a !== void 0 ? _a : [])
                .filter((r) => r.assetId === mewsAssetId && r.ref)
                .map((r) => { var _a; return (_a = r.saleItemUuid) !== null && _a !== void 0 ? _a : ''; });
            setMappedOptionIds(ids);
        }
    });
    useEffect(() => {
        if (dealId) {
            fetchDealConnectorRef({ variables: { input: { dealId } } });
        }
        if (mainSaleItemIds.length > 0) {
            fetchMainRefs({ variables: { input: { saleItems: mainSaleItemIds } } });
        }
        if (optionSaleItemIds.length > 0) {
            fetchOptionRefs({
                variables: { input: { saleItems: optionSaleItemIds } }
            });
        }
    }, [dealId]);
    // DealItems that have a date
    const datedItems = dealItems.filter((di) => di.date);
    // Unique option SaleItems (columns) — those with a connector ref, deduplicated by item.id
    const seenOptionIds = new Set();
    const uniqueOptionItems = dealItems
        .flatMap((di) => { var _a; return (_a = di.options) !== null && _a !== void 0 ? _a : []; })
        .filter((o) => {
        var _a, _b;
        const id = (_b = (_a = o === null || o === void 0 ? void 0 : o.item) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '';
        if (!mappedOptionIds.includes(id) || seenOptionIds.has(id))
            return false;
        seenOptionIds.add(id);
        return true;
    });
    const rateOptions = (_c = (_b = (_a = ratesData === null || ratesData === void 0 ? void 0 : ratesData.getMewsRates) === null || _a === void 0 ? void 0 : _a.rates) === null || _b === void 0 ? void 0 : _b.map((rate) => ({
        value: rate.id,
        label: rate.name
    }))) !== null && _c !== void 0 ? _c : [];
    const loading = loadingConnectorRef || loadingOptionRefs;
    const title = (React.createElement("span", null,
        t('Mews'),
        " ",
        React.createElement("span", { className: "dmab-modal__beta" }, t('Beta'))));
    const footerActions = (React.createElement(Fragment, null,
        React.createElement(Button, { label: t('Skip'), onPress: onClose }),
        React.createElement(Button, { style: "primary", label: existingBlockRef ? t('Update') : t('Create'), onPress: () => onConfirm(rateId) })));
    function renderTable() {
        if (datedItems.length === 0 || uniqueOptionItems.length === 0) {
            return null;
        }
        return (React.createElement("div", { className: "dmab-modal__table-wrap" },
            React.createElement("table", { className: "dmab-modal__table" },
                React.createElement("thead", null,
                    React.createElement("tr", null,
                        React.createElement("th", { className: "dmab-modal__th dmab-modal__th--date" }, t('Date')),
                        uniqueOptionItems.map((item) => {
                            var _a;
                            return (React.createElement("th", { key: item === null || item === void 0 ? void 0 : item.id, className: "dmab-modal__th" }, (_a = item === null || item === void 0 ? void 0 : item.item) === null || _a === void 0 ? void 0 : _a.title));
                        }))),
                React.createElement("tbody", null, datedItems.map((di) => (React.createElement("tr", { key: di.id, className: "dmab-modal__tr" },
                    React.createElement("td", { className: "dmab-modal__td dmab-modal__td--date" }, di.date),
                    uniqueOptionItems.map((item) => {
                        var _a, _b, _c;
                        const option = ((_a = di.options) !== null && _a !== void 0 ? _a : []).find((o) => { var _a, _b; return ((_a = o.item) === null || _a === void 0 ? void 0 : _a.id) === ((_b = item === null || item === void 0 ? void 0 : item.item) === null || _b === void 0 ? void 0 : _b.id); });
                        const qty = (_b = option === null || option === void 0 ? void 0 : option.quantity) !== null && _b !== void 0 ? _b : 0;
                        return (React.createElement("td", { key: (_c = item === null || item === void 0 ? void 0 : item.item) === null || _c === void 0 ? void 0 : _c.id, className: "dmab-modal__td" }, qty > 0 ? (qty) : (React.createElement("span", { className: "dmab-modal__td-empty" }, "\u2014"))));
                    }))))))));
    }
    function renderContent() {
        if (loading) {
            return (React.createElement("div", { className: "dmab-modal__loading" },
                React.createElement(Spinner, null)));
        }
        const hint = existingBlockRef
            ? t('An availability block already exists in Mews for this deal. Confirming will update the allocations.')
            : t('An availability block will be created in Mews for this deal.');
        return (React.createElement("div", { className: "dmab-modal__form" },
            existingBlockRef && (React.createElement("div", { className: "dmab-modal__existing-badge" },
                React.createElement("i", { className: "fal fa-circle-check dmab-modal__existing-icon" }),
                React.createElement("span", null, t('Block already created')))),
            React.createElement("p", { className: "dmab-modal__hint" }, hint),
            renderTable(),
            !existingBlockRef && (React.createElement("div", { className: "dmab-modal__rate" },
                React.createElement(FormSelect, { name: "mewsRate", label: t('Rate'), placeholder: loadingRates ? t('Loading...') : t('Select a rate (optional)'), options: rateOptions, value: rateId, onChange: (value) => setRateId(value), search: true, optional: true })))));
    }
    return (React.createElement("div", { className: "deal-mews-availability-block-modal" },
        React.createElement(Modal, { picto: "calendar", title: title, onClose: onClose, showFooterAction: true, footerActions: footerActions },
            React.createElement("div", { className: "deal-mews-availability-block-modal-content" },
                React.createElement("div", { className: "dmab-modal__beta-notice" },
                    React.createElement("i", { className: "fal fa-triangle-exclamation dmab-modal__beta-notice-icon" }),
                    React.createElement("p", null, t('This feature is in beta. Please carefully verify the data on both sides (Centralio and Mews) before confirming.'))),
                renderContent()))));
}
